import { useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { AlertTriangle } from 'lucide-react';
import { useGetList } from 'react-admin';
import { Link } from 'react-router-dom';

interface DuplicateMatch {
    id: string | number;
    label: string;
    resource: string;
}

// Hook for account duplicate checking
export function useAccountDuplicateCheck(name: string) {
    const [debouncedName, setDebouncedName] = useState('');
    const timerRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        clearTimeout(timerRef.current);
        const trimmed = name.trim();
        if (trimmed.length < 2) {
            setDebouncedName('');
            return;
        }
        timerRef.current = setTimeout(() => setDebouncedName(trimmed), 400);
        return () => clearTimeout(timerRef.current);
    }, [name]);

    const { data } = useGetList(
        'accounts',
        {
            pagination: { page: 1, perPage: 5 },
            filter: debouncedName ? { q: debouncedName } : undefined,
        },
        { enabled: debouncedName.length >= 2 }
    );

    const matches: DuplicateMatch[] = (data || [])
        .filter(
            (a: { name?: string }) =>
                a.name &&
                a.name.toLowerCase().includes(debouncedName.toLowerCase())
        )
        .map((a: { id: string | number; name?: string }) => ({
            id: a.id,
            label: a.name || '',
            resource: 'accounts',
        }));

    return matches;
}

// Hook for contact duplicate checking
export function useContactDuplicateCheck(
    firstName: string,
    lastName: string,
    email?: string
) {
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const timerRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        clearTimeout(timerRef.current);
        const first = firstName.trim();
        const last = lastName.trim();
        const query = last.length >= 2 ? `${first} ${last}`.trim() : '';
        if (query.length < 2) {
            setDebouncedQuery('');
            return;
        }
        timerRef.current = setTimeout(() => setDebouncedQuery(query), 400);
        return () => clearTimeout(timerRef.current);
    }, [firstName, lastName]);

    const { data: nameMatches } = useGetList(
        'contacts',
        {
            pagination: { page: 1, perPage: 10 },
            filter: debouncedQuery ? { q: debouncedQuery } : undefined,
        },
        { enabled: debouncedQuery.length >= 2 }
    );

    const [debouncedEmail, setDebouncedEmail] = useState('');
    const emailTimerRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        clearTimeout(emailTimerRef.current);
        const trimmed = (email || '').trim();
        if (trimmed.length < 5 || !trimmed.includes('@')) {
            setDebouncedEmail('');
            return;
        }
        emailTimerRef.current = setTimeout(
            () => setDebouncedEmail(trimmed),
            400
        );
        return () => clearTimeout(emailTimerRef.current);
    }, [email]);

    const { data: emailMatches } = useGetList(
        'contacts',
        {
            pagination: { page: 1, perPage: 5 },
            filter: debouncedEmail ? { q: debouncedEmail } : undefined,
        },
        { enabled: debouncedEmail.length >= 5 }
    );

    const seen = new Set<string | number>();
    const matches: DuplicateMatch[] = [];
    const first = firstName.trim().toLowerCase();
    const last = lastName.trim().toLowerCase();
    const emailLower = debouncedEmail.toLowerCase();

    for (const c of nameMatches || []) {
        const cf = (
            (c as { first_name?: string }).first_name || ''
        ).toLowerCase();
        const cl = (
            (c as { last_name?: string }).last_name || ''
        ).toLowerCase();
        if (cf === first && cl === last && !seen.has(c.id)) {
            seen.add(c.id);
            matches.push({
                id: c.id,
                label: `${(c as { first_name?: string }).first_name || ''} ${(c as { last_name?: string }).last_name || ''}`.trim(),
                resource: 'contacts',
            });
        }
    }

    if (emailLower) {
        for (const c of emailMatches || []) {
            if (seen.has(c.id)) continue;
            const emails = (c as { email_jsonb?: { email: string }[] })
                .email_jsonb;
            if (
                emails?.some(
                    (e: { email: string }) =>
                        e.email.toLowerCase() === emailLower
                )
            ) {
                seen.add(c.id);
                matches.push({
                    id: c.id,
                    label: `${(c as { first_name?: string }).first_name || ''} ${(c as { last_name?: string }).last_name || ''}`.trim(),
                    resource: 'contacts',
                });
            }
        }
    }

    return matches;
}

// Reusable warning banner component
export function DuplicateWarning({
    matches,
    entityType,
}: {
    matches: DuplicateMatch[];
    entityType: 'account' | 'contact';
}) {
    if (matches.length === 0) return null;

    return (
        <Box
            sx={{
                p: 1.5,
                borderRadius: '8px',
                bgcolor: '#fefce8',
                border: '1px solid #fde68a',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
            }}
        >
            <AlertTriangle
                size={16}
                color="#d97706"
                style={{ marginTop: 2, flexShrink: 0 }}
            />
            <Box>
                <Typography
                    sx={{
                        fontSize: '0.8125rem',
                        fontWeight: 600,
                        color: '#92400e',
                    }}
                >
                    Possible duplicate{matches.length > 1 ? 's' : ''} found
                </Typography>
                <Typography
                    sx={{
                        fontSize: '0.75rem',
                        color: '#a16207',
                        mt: 0.25,
                    }}
                >
                    {matches.length === 1
                        ? `An ${entityType} with a similar name already exists:`
                        : `${matches.length} ${entityType}s with similar names already exist:`}
                </Typography>
                <Box
                    component="ul"
                    sx={{
                        m: 0,
                        mt: 0.5,
                        pl: 2,
                        listStyle: 'none',
                    }}
                >
                    {matches.slice(0, 3).map(match => (
                        <Box component="li" key={match.id} sx={{ mb: 0.25 }}>
                            <Link
                                to={`/${match.resource}/${match.id}/show`}
                                target="_blank"
                                style={{
                                    fontSize: '0.8125rem',
                                    fontWeight: 500,
                                    color: '#d97706',
                                    textDecoration: 'underline',
                                }}
                            >
                                {match.label}
                            </Link>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}
